import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, Library, CalendarClock, Users, Church } from 'lucide-react'
import { capability, VERTICAL_STACKS } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const musicManager = capability('music-manager')!
const churchStack = VERTICAL_STACKS.find((v) => v.id === 'church')!

const PROBLEMS = [
  {
    problem: 'The library lives in a spreadsheet',
    description:
      'What the choir sang, when, in whose arrangement, and how it went lives in one long spreadsheet, or in the director’s head. Repertoire planning starts from zero every season.',
  },
  {
    problem: 'Rehearsal orders live in email chains',
    description:
      'The order changes Thursday night, half the ensemble reads the old thread, and the accompanist finds out at the piano.',
  },
  {
    problem: 'Personnel is a group text',
    description:
      'Who is in this week, who needs a sub, who covered last time: coverage runs on memory and last-minute messages.',
  },
  {
    problem: 'The tools are not built for ensembles',
    description:
      'Project managers do not know what an anthem, a service, or a voice part is. Directors end up as the integration layer.',
  },
]

const FEATURES = [
  {
    icon: Library,
    title: 'Music library',
    description:
      'Your repertoire with history attached: every piece, every performance date, every edition and arrangement, searchable when you plan the next season.',
  },
  {
    icon: CalendarClock,
    title: 'Rehearsal planning',
    description:
      'Rehearsal orders and service plans in one place, current for everyone the moment they change. No more competing email threads.',
  },
  {
    icon: Users,
    title: 'Ensemble management',
    description:
      'Rosters and voice parts today; the Pro tier adds attendance, multi-ensemble support, substitute management, and automations.',
  },
]

const FAQS = [
  {
    q: 'What does it cost?',
    a: `$${musicManager.priceMonthly} a month for the base: music library, rehearsal planning, and roster management. Pro is $${musicManager.proMonthly} and adds attendance, multi-ensemble, substitute management, and automations. Pay annually and get 2 months free.`,
  },
  {
    q: 'Is this available today?',
    a: 'It runs every week in production, and we are onboarding early-access ensembles hands-on: our team sets it up with you and stays close. Book a call and we will tell you honestly whether your situation is a fit right now.',
  },
  {
    q: 'What is Canticle?',
    a: 'Canticle is the church layer of Music Manager: liturgy- and lectionary-aware planning, service music, hymns, and personnel for parish programs. Churches get everything above, plus the layer that speaks their calendar.',
  },
  {
    q: 'Does it require the rest of Fugue Lab?',
    a: 'No. Music Manager stands on its own. It also composes with the church stack (website, email, donor management) if you want the whole program on one platform with one audience record.',
  },
]

export default function MusicManagerPage() {
  const breadcrumbs = generateBreadcrumbs('/music-manager', breadcrumbLabels)

  useSEO({
    title: 'Fugue Lab Music Manager: Music Library, Rehearsal Planning, Ensemble Management',
    description:
      'Music library with performance history, rehearsal planning, and ensemble management for churches, choruses, and schools. $30/mo base, $75 Pro. Canticle is the church layer.',
    url: '/music-manager',
    canonical: '/music-manager',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Fugue Lab Music Manager',
      description:
        'Music library, rehearsal planning, and ensemble management software for churches, choruses, and music schools, with the Canticle liturgy layer for parishes.',
      url: '/music-manager',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <div className="bg-primary/10 text-primary mx-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
          Early access: onboarded hands-on with our team
        </div>
        <h1 className="text-fluid-h1">The music side of running an ensemble, finally in one place</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Fugue Lab Music Manager is the music library, rehearsal planner, and ensemble roster for churches, choruses,
          and schools. Built by a working church musician, and used to plan real services every week.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          ${musicManager.priceMonthly} a month, ${musicManager.proMonthly} for Pro. For parishes it wears the name
          Canticle, with a liturgy layer that speaks your calendar.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Ask About Early Access →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">The admin that eats the music</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">What it does</h2>
        <div ref={featuresRef} className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
          {FEATURES.map((f) => (
            <motion.div
              key={f.title}
              className="feature-card border-border flex flex-col rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <f.icon className="text-primary mb-4 h-8 w-8" />
              <h3 className="text-foreground text-xl font-semibold">{f.title}</h3>
              <p className="text-muted-foreground mt-3 text-sm">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Canticle: the church layer */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8">
        <div className="flex items-center justify-center gap-3">
          <Church className="text-primary h-7 w-7" />
          <h2 className="text-fluid-h2 text-center">For churches, it is called Canticle</h2>
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center">
          Parish music lives on a calendar the rest of the software world ignores. Canticle is the church layer of Music
          Manager: liturgy- and lectionary-aware service planning, hymns and service music alongside anthems, and the
          personnel picture (section leaders, scholars, substitutes) that makes Sunday happen.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center text-sm">
          It is not a demo: Canticle plans the choral services at Holy Trinity Episcopal Church every week, run by the
          director who built it.
        </p>
      </section>

      {/* Pricing + church stack */}
      <section className="mx-auto max-w-4xl space-y-6">
        <h2 className="text-fluid-h2 text-center">Pricing</h2>
        <div className="grid gap-8 md:grid-cols-2">
          <motion.div
            className="border-primary/50 bg-primary/5 rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <h3 className="mb-2 text-2xl font-bold">{musicManager.name}</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">${musicManager.priceMonthly}</span>
              <span className="text-muted-foreground">/month · Pro ${musicManager.proMonthly}</span>
            </div>
            <ul className="mb-4 space-y-3">
              {[
                'Music library with performance history',
                'Rehearsal planning and service orders',
                'Roster and voice parts',
                'Pro: attendance, multi-ensemble, sub management, automations',
                'Canticle church layer included for parishes',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground text-xs">
              Early access: our team onboards you hands-on. Pay annually and get 2 months free.
            </p>
          </motion.div>
          <motion.div
            className="border-border rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <h3 className="mb-2 text-2xl font-bold">The church stack</h3>
            <p className="text-muted-foreground mb-4 text-sm">{churchStack.note}</p>
            <ul className="mb-4 space-y-3">
              {[
                'Website from $25/mo',
                'Email Marketing $20/mo',
                'Donor Management $25/mo (accepting donations is free)',
                `${musicManager.name} $${musicManager.priceMonthly}/mo`,
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/packages" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                See All Pricing
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Ask About Early Access →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
